import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

import { VersionsViewService } from '@app/services/app/versions.service';
import { AppRouterService } from '@app/services/app/router.service';
import { AppService } from '@app/services/app/app.service';

import { UrlService } from '@core/services/url/url.service';
import { TitleService } from '@core/services/title.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';

import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

import { ShareVersionFeedback } from '@core/models/version-feedback';
import { VersionShareEntryDetail, isFeedbackVersionShare, getVersionShareFiles } from '@core/models/version-share.model';
/**
 * NOTICE: after jan 2026 this component is not used anymore, but we keep it 
 * because redirects to the version urls under project router.
 * 
 * Content is never used, but the component is used in project router.
 */  
@Component({
    selector: 'version-details-file-preview-main',
    templateUrl: './version-file-preview.component.html',
    styleUrls: ['./version-file-preview.component.scss']
})
export class VersionsDetailsFilePreviewComponent implements OnInit, OnDestroy {

    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly PLUGIN_FEEDBACK = 'share_feedback';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;
    public readonly STATUS_FILE_NOT_FOUND = 4;
    public readonly STATUS_NOT_ALLOWED= 5;
    public readonly STATUS_NOT_FOUND= 6;

    public version: Objects.Version = null;
    public file: Objects.AmsFile = null;
    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public originProject: Objects.Request = null;
    public notesUrlPrefix: string = ''
    public canCreateNotes: boolean = false;

    public sharedFileIds: Array<number> = null;

    public context: string = '';

    public breadcrumbs: any = [{
        link: "/",
        title: 'Artwork v. . . .  <small>(#  .  .  . )</small>'
    }];

    public status:number = this.STATUS_LOADING;
    public menu: Array<ThreeDotsMenuItem> = [{
        title: "Download File",
        key: "download",
        icon: "download",
        order: 1
    }];


    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 2
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 3
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 4
    };
    public menuPdfWithComments: ThreeDotsMenuItem = {
        title: "Download with comments",
        key: "download-comments",
        icon: "download_comments",
        order: 5
    };

    protected urlSubscriptor: Subscription;
    protected urlParamsSubscriptor: Subscription;
    protected filesSubscriptor: Subscription;
    protected versionSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected originProjectSubscriptor: Subscription;
    protected errorSubscriptor: Subscription;
    protected shareVersionSubscriptor: Subscription;

    constructor(private title: TitleService,
        private app: AppService,
        private urlSrv: UrlService,
        private appRouter: AppRouterService,
        private activeRoute: ActivatedRoute,
        private notifSrv: NotificationsService,
        private viewVersionsSrv: VersionsViewService) { }

    ngOnInit() {
        this.title.set("Preview File... - Artwork...");

        this.urlSubscriptor = this.activeRoute.queryParams.subscribe((params: any) => {
            // console.log("query params ===>", params);
            if (params.context) {
                this.context = params.context;
            }
        });

        // get the version
        this.versionSubscriptor = this.viewVersionsSrv.selectVersion().subscribe((version: Objects.Version) =>
        {
            // console.log(" VERSION LOADED in file PREVIEW ",version);
            this.version = version;
            this.initView();
        });

        this.originProjectSubscriptor = this.viewVersionsSrv.selectProject().subscribe((projectOrigin: Objects.Request) => {
            this.originProject = projectOrigin;
        });

        this.urlParamsSubscriptor = this.activeRoute.params.subscribe((params: any) => {
            let fid: any = +params.fid;
            this.filesSubscriptor = this.viewVersionsSrv.selectAssets().subscribe((assets: Objects.AmsFile[] ) => {
                if(assets.length > 0){
                    let match = assets.filter((f: Objects.AmsFile) => {return f.id == fid;});
                    if (match.length > 0 ){
                        this.file = match[0];
                    } else {
                        this.status = this.STATUS_FILE_NOT_FOUND;
                    }
                    this.initView();
                }

            });

        });


        this.appSubscriptor = combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe( ([session, accountSession]: [any, AccountSession]) => {
            this.pluginsAccess = session.options.plugins;
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
            this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            this.updateMenu(this.menuSendFiles,this.checkPluginAccess(this.PLUGIN_SEND_FILES))
            this.updateMenu(this.menuCompare,this.checkCompareAccess());
            this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
            if(this.checkPluginAccess(this.PLUGIN_FEEDBACK)){
                this.loadSharedFiles();
            }
        });

        this.errorSubscriptor = this.viewVersionsSrv.selectError('load_version').subscribe((error: HttpErrorResponse) => {
            this.setError(error);
        });
    }

    ngOnDestroy() {
        this.urlSubscriptor.unsubscribe();
        this.urlParamsSubscriptor.unsubscribe();
        this.versionSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
        if(this.filesSubscriptor)
            this.filesSubscriptor.unsubscribe();
        this.originProjectSubscriptor.unsubscribe();
        this.errorSubscriptor.unsubscribe();
        if (this.shareVersionSubscriptor){
            this.shareVersionSubscriptor.unsubscribe();
        }
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                // start notification to download the pdf with the annotations in it. We need the url for the prefix of the download and the file.
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.version.id, details: { url: "/versions/"+this.version.id+ "/feedbacks", amsfile: menu.file}});
                break;
            case "download":
                window.location.href = this.urlSrv.build('/files/'+menu.file.id);
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES) && this.originProject != null){
                    this.appRouter.navigate('/p/transfer/'+this.originProject.id, { queryParams: { file: +menu.file.id, version: this.version.id} });
                }
                break;
            case "compare":
                if (this.originProject != null)
                    this.appRouter.navigate('/p/apollo/request/'+this.originProject.id);
                break;
            case "comparison":
                if (this.originProject != null)
                    this.appRouter.navigate('/filecomparison/project/'+this.originProject.id+'/artwork/'+this.version.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    private initView() {
        if(this.version != null)
            this.updateBreadcrumbs();
        if(this.version == null || this.file == null)
            return;

        // prefix to get files notes
        this.notesUrlPrefix = "/p/feedback/versions/" + this.version.id;
        this.title.set("Preview File "+this.file.name+ " - Artwork #"+ this.version.id);
        this.status = this.STATUS_DONE;
    }

    protected updateBreadcrumbs(){ // update intermediate breadcrumb depending on context
        switch (this.context) {
            case "other":
                break;
            default:
                this.breadcrumbs[0] = {link: "/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                break;
        }
    }

    protected setError(error: HttpErrorResponse) {
        // console.log("we have file or version errors ",error)
        if(error == null)
            return;
        switch(error.status) {
            case 500:
                this.status = this.STATUS_ERROR;
                break;
            case 403:
                this.viewVersionsSrv.clearAssets();
                this.file = null;
                this.status = this.STATUS_NOT_ALLOWED;
                break;
        }
    }
    // load share to get sahred files for this version in case to show notesn instead
    protected loadSharedFiles() : void {
        // @deprecated oct-2026: legacy share feedback url call, replaced by selectMyVersionShares
        // this.shareVersionSubscriptor = this.viewVersionsSrv.selectShareFeedback().subscribe((shareVersionFeedback: ShareVersionFeedback) => {
        //     // here comes the shareVersionFeedback with properties:
        //     if(shareVersionFeedback != null){
        //         // this.share = shareVersionFeedback.share;
        //         if( shareVersionFeedback.share.entry &&
        //             shareVersionFeedback.share.entry.share &&
        //             shareVersionFeedback.share.entry.share.properties )
        //         {
        //             let filesProp = shareVersionFeedback.share.entry.share.properties.filter((p:any) => p.key == 'files');
        //             this.sharedFileIds = filesProp.length > 0 ? (filesProp[0]).value : [];
        //         }
        //     }
        // });
        this.shareVersionSubscriptor = this.viewVersionsSrv.selectMyVersionShares().subscribe((myShares: Array<VersionShareEntryDetail>) => {
            if(myShares != null){
                const myShare = myShares.find((ms: VersionShareEntryDetail) => isFeedbackVersionShare(ms)) ?? null;
                this.sharedFileIds = getVersionShareFiles(myShare);
            }
        });
    }

}
